// apps/roleforge-app/src/components/audit/AuditPersistentShell.tsx
//
// Plan 3e-i + Layout rework: Thin wrapper that composes the three-column
// audit shell. The header (StepIndicator) now lives in the sidebar, the
// chat-or-asset middle column is XOR (one or the other), and the operating
// model lives as cards in the right column instead of a left-side panel.

import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AuditRow,
  AuditStatus,
  Message,
  Opportunity,
  OpsCanvasEngineRow,
  ChangeProposal,
  ThreadSummary,
  RoleforgeAgentProfile,
} from '../../lib/api-client';
import { listThreads } from '../../lib/api-client';
import type { SseEventRouter } from '../../lib/sse-event-router';
import { useOperatingModelState } from '../../lib/use-operating-model-state';
import { useActivityStream } from '../../lib/use-activity-stream';
import { AuditAppShell } from '../layout/AuditAppShell';
import { AuditConverseColumn } from './AuditConverseColumn';
import { AssetsColumn, type AssetId } from './AssetsColumn';
import { RightColumnTabs } from './RightColumnTabs';
import { VoicesTab } from './VoicesTab';
import type { ActivationMomentLayoutProps } from './ActivationMomentLayout';

export type { ActivationMomentLayoutProps } from './ActivationMomentLayout';

export type ShellMode = 'building' | 'validating' | 'locked' | 'materializing' | 'materialized' | 'failed';

function statusToStep(status: AuditStatus): 1 | 2 | 3 | 4 {
  switch (status) {
    case 'draft':
    case 'interviewing':
      return 1;
    case 'synthesizing':
      return 2;
    case 'validating':
      return 3;
    case 'ready_to_materialize':
    case 'materializing':
    case 'materialized':
    case 'failed':
      return 4;
    default:
      return 1;
  }
}

function statusToMode(status: AuditStatus): ShellMode {
  switch (status) {
    case 'draft':
    case 'interviewing':
    case 'synthesizing':
      return 'building';
    case 'validating':
      return 'validating';
    case 'ready_to_materialize':
      return 'locked';
    case 'materializing':
      return 'materializing';
    case 'materialized':
      return 'materialized';
    case 'failed':
      return 'failed';
    default:
      return 'building';
  }
}

interface Props {
  audit: AuditRow;
  router: SseEventRouter;
  refreshAudit: () => Promise<void>;
  messages: Message[];
  agentProfile?: RoleforgeAgentProfile | null;
  opsCanvas?: OpsCanvasEngineRow[];
  opportunities?: Opportunity[];
  jwt?: string | null;
  streamingAgentText: string;
  onSend?: (text: string) => void;
  composerDisabled?: boolean;
  composerPlaceholder?: string;
  /**
   * When `mode === 'locked'` the converse column renders the Activation
   * Moment layout instead of the chat surface. If this is undefined while
   * locked, the converse column shows a small loading placeholder.
   */
  activationData?: ActivationMomentLayoutProps;
  changeProposals?: ChangeProposal[];
  onApproveProposal?: (proposalId: string, mode: 'later' | 'now') => Promise<void>;
  /** Currently-open asset (right-column → middle-column). */
  activeAssetId?: AssetId | null;
  onSelectAsset?: (id: AssetId | null) => void;
}

export function AuditPersistentShell({
  audit,
  router,
  refreshAudit,
  messages,
  agentProfile,
  opsCanvas,
  opportunities,
  jwt,
  streamingAgentText,
  onSend,
  composerDisabled,
  composerPlaceholder,
  activationData,
  changeProposals,
  onApproveProposal,
  activeAssetId: controlledActiveAssetId,
  onSelectAsset,
}: Props) {
  const step = statusToStep(audit.status);
  const mode = statusToMode(audit.status);

  // Single shared subscription for both the assets column and (when opened)
  // the asset editor pane in the middle column.
  //
  // #1329: memoized. This object literal was rebuilt on every render, and the
  // hook's reset effect depended on its members' identity — so after every send
  // the effect fired and discarded live SSE state (cards that had just animated
  // in blinked out). The hook now also keys on a revision string rather than
  // identity, which is the real fix; this memo just stops handing it churn.
  const persistedOperatingModel = useMemo(
    () => ({ audit, opsCanvas, opportunities }),
    [audit, opsCanvas, opportunities],
  );
  const operatingModelState = useOperatingModelState(router, persistedOperatingModel);
  const activityItems = useActivityStream(router, { auditStatus: audit.status });

  // Allow uncontrolled use (e.g. tests render the shell directly without a
  // page wrapping it); fall back to internal state if no controller.
  const [internalActiveAssetId, setInternalActiveAssetId] = useState<AssetId | null>(null);
  const activeAssetId = controlledActiveAssetId ?? internalActiveAssetId;
  const setActiveAssetId = (id: AssetId | null) => {
    if (onSelectAsset) onSelectAsset(id);
    else setInternalActiveAssetId(id);
  };

  // Phase 11 — fetch threads at the shell level so we can resolve voice
  // labels for the AttributedAssetsPane (rendered in the middle column).
  // VoicesTab fetches its own copy independently for now; the double-fetch
  // is intentional to keep VoicesTab self-contained.
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!jwt) {
      return () => {
        cancelled = true;
      };
    }
    void listThreads(jwt, audit.id)
      .then((res) => {
        if (!cancelled) setThreads(res.threads);
      })
      .catch(() => {
        // Non-fatal; the resolver will fall back to "Voice" for unknown ids.
      });
    return () => {
      cancelled = true;
    };
  }, [jwt, audit.id]);
  const resolveVoiceLabel = useCallback(
    (threadId: string) =>
      threads.find((t) => t.id === threadId)?.voice_label,
    [threads],
  );

  const handleSelectVoice = (threadId: string) => {
    setActiveAssetId(`voice:${threadId}` as AssetId);
  };

  // Forge synthesis burst — overlays the converse column while the agent
  // fires its synthesis tool calls. Activates the first time an
  // `engine.created` event arrives during the Forge fork's interviewing
  // phase; deactivates when the burst calls onDone().
  const [synthBurstActive, setSynthBurstActive] = useState(false);
  useEffect(() => {
    if (audit.audit_fork !== 'forge') return;
    if (audit.status !== 'interviewing' && audit.status !== 'synthesizing') return;
    const unsub = router.subscribe('operating_model', 'engine.created', () => {
      setSynthBurstActive(true);
    });
    return unsub;
  }, [router, audit.audit_fork, audit.status]);

  // Phase 11 — the right column is a tabbed shell: Assets (default) +
  // Voices. Rendered identically across the three layout branches below.
  const rightColumn = (
    <RightColumnTabs
      assetsContent={
        <AssetsColumn
          state={operatingModelState}
          activeAssetId={activeAssetId}
          onSelectAsset={setActiveAssetId}
        />
      }
      voicesContent={
        <VoicesTab auditId={audit.id} jwt={jwt} onSelectVoice={handleSelectVoice} />
      }
    />
  );

  return (
    <AuditAppShell
      currentStep={step}
      auditId={audit.id}
      workspaceId={audit.workspace_id}
      converseColumn={
        <AuditConverseColumn
          audit={audit}
          jwt={jwt}
          router={router}
          refreshAudit={refreshAudit}
          mode={mode}
          messages={messages}
          agentProfile={agentProfile}
          streamingAgentText={streamingAgentText}
          activityItems={activityItems}
          onSend={onSend}
          composerDisabled={composerDisabled}
          composerPlaceholder={composerPlaceholder}
          activationData={activationData}
          changeProposals={changeProposals}
          onApproveProposal={onApproveProposal}
          activeAssetId={activeAssetId}
          onCloseAsset={() => setActiveAssetId(null)}
          operatingModelState={operatingModelState}
          synthesisBurstActive={synthBurstActive}
          onSynthesisBurstDone={() => setSynthBurstActive(false)}
          resolveVoiceLabel={resolveVoiceLabel}
        />
      }
      assetsColumn={rightColumn}
    />
  );
}
